import React from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import KenUploadButton from './Components/KenUploadButton';

const useStyles = makeStyles(() => ({
  rowWrap: { marginBottom: 30 },
  rowWrapConditional: { borderBottom: 0 },
  rowLabel: { fontWeight: 500, color: '#091E42' },
  rowDesc: { maxWidth: '400px', color: '#7A869A' },
}));

export default function KenDocumentUpload(props) {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedFileName, setUploadedFileName] = React.useState('');

  const {
    value = [],
    label,
    description,
    onChange,
    length,
    index,
    required,
    applicationDataState,
    recordId,
    handleDownload,
    fileType,
    fileName,
    fieldId,
  } = props;

  const classes = useStyles();

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file); // ✅ keep full data URL
    reader.onload = () => resolve(reader.result); // ✅ return full data URL (with "data:...")
    reader.onerror = (err) => reject(err);
  });


  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    multiple: false,
    noKeyboard: true,
    maxSize: 5 * 1024 * 1024,
 onDrop: async (acceptedFiles, fileRejections) => {
  if (fileRejections.length > 0) {
    enqueueSnackbar(fileRejections[0]?.errors[0]?.message, { variant: 'error' });
    return;
  }

  const file = acceptedFiles[0];
  if (!file) return;

  const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

  // ❌ Block all video file types
  const videoTypes = [
    'video/mp4',
    'video/mpeg',
    'video/ogg',
    'video/webm',
    'video/avi',
    'video/quicktime',
  ];
  if (videoTypes.includes(file.type)) {
    enqueueSnackbar('Video files are not allowed.', { variant: 'error' });
    return;
  }

  // Allowed types
  const allowedResumeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const allowedPhotoTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  if (fieldId === 'Resume') {
    if (!allowedResumeTypes.includes(file.type)) {
      enqueueSnackbar('Only PDF, DOC, or DOCX files are allowed for Resume.', {
        variant: 'error',
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('Resume file size must not exceed 5 MB.', { variant: 'error' });
      return;
    }
  } else if (fieldId === 'passportSizePhotograph') {
    if (!allowedPhotoTypes.includes(file.type)) {
      enqueueSnackbar('Only JPG, JPEG, or PNG images are allowed for Photograph.', {
        variant: 'error',
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar('Photograph size must not exceed 2 MB.', { variant: 'error' });
      return;
    }
  }

  // Rename file
  let renamedFileName = file.name;
  switch (fieldId) {
    case 'Resume':
      renamedFileName = 'Resume' + extension;
      break;
    case 'passportSizePhotograph':
      renamedFileName = 'Photo' + extension;
      break;
    case 'qualificationUpload':
      renamedFileName = 'Qualification' + extension;
      break;
    case 'adharCardUpload':
      renamedFileName = 'Adhar' + extension;
      break;
    case 'panCardUpload':
      renamedFileName = 'Pan' + extension;
      break;
    case 'passbookCopy':
      renamedFileName = 'Bank' + extension;
      break;
    default:
      renamedFileName = file.name;
  }

  // Convert to Base64
  const base64 = await toBase64(file);

  const newFileObj = {
    fieldId,
    fileName: renamedFileName,
    base64,
    type: file.type,
  };

  onChange([...value, newFileObj]);
  setUploadedFileName(renamedFileName);
  enqueueSnackbar('File selected successfully', { variant: 'success' });
},
  });

  return (
    <div
      className={clsx(classes.rowWrap, {
        [classes.rowWrapConditional]: length === index,
      })}
    >
      <Grid container {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <Grid item xs={12} md={8}>
          <Typography align="left" className={classes.rowLabel} variant="h5">
            {label}
            {required && <span style={{ color: '#E75547' }}>*</span>}
          </Typography>
          <Typography align="left" className={classes.rowDesc} variant="subtitle2">
            {description}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
        <KenUploadButton
  open={open}
  applicationStateData={applicationDataState}
  recordId={recordId}
  handleDownload={handleDownload}
  fileType={fileType}
  fileName={uploadedFileName || fileName}
  fileBase64={value.find(f => f.fieldId === fieldId)?.base64} // <-- pass base64
/>
        </Grid>
      </Grid>
    </div>
  );
}
